/**
 * useSubtaskMutations — Mutation hooks for subtask CRUD operations.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useAuth } from "./useAuth";

export const useSubtaskMutations = () => {
  const { handleError, handleSuccess } = useErrorHandler();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const addSubtaskMutation = useMutation({
    mutationFn: async ({ todoId, subtaskText, subtaskDueDate }: { todoId: string, subtaskText: string, subtaskDueDate?: string }) => {
      if (!user) throw new Error("User must be authenticated");

      const { data, error } = await supabase
        .from('subtasks')
        .insert([{
          todo_id: todoId,
          text: subtaskText,
          due_date: subtaskDueDate,
          completed: false
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      handleSuccess("New step has been added successfully.", { title: "Step added" });
    },
    onError: (error: Error) => {
      handleError(error, { title: "Error adding step" });
    }
  });

  const toggleSubtaskMutation = useMutation({
    mutationFn: async ({ subtaskId }: { subtaskId: string }) => {
      if (!user) throw new Error("User must be authenticated");

      const { data: subtask } = await supabase
        .from('subtasks')
        .select('completed')
        .eq('id', subtaskId)
        .single();

      const { data, error } = await supabase
        .from('subtasks')
        .update({ completed: !subtask?.completed })
        .eq('id', subtaskId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
    },
    onError: (error: Error) => {
      handleError(error, { title: "Error toggling step" });
    }
  });

  const deleteSubtaskMutation = useMutation({
    mutationFn: async ({ subtaskId }: { subtaskId: string }) => {
      if (!user) throw new Error("User must be authenticated");

      const { error } = await supabase
        .from('subtasks')
        .delete()
        .eq('id', subtaskId);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      handleSuccess("Step has been deleted successfully.", { title: "Step deleted" });
    },
    onError: (error: Error) => {
      handleError(error, { title: "Error deleting step" });
    }
  });

  const updateSubtaskDueDateMutation = useMutation({
    mutationFn: async ({ subtaskId, newDate }: { subtaskId: string, newDate: string }) => {
      if (!user) throw new Error("User must be authenticated");

      const { data, error } = await supabase
        .from('subtasks')
        .update({ due_date: newDate })
        .eq('id', subtaskId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      handleSuccess("Step due date has been updated successfully.", { title: "Due date updated" });
    },
    onError: (error: Error) => {
      handleError(error, { title: "Error updating due date" });
    }
  });

  const updateSubtaskTextMutation = useMutation({
    mutationFn: async ({ subtaskId, newText }: { subtaskId: string, newText: string }) => {
      if (!user) throw new Error("User must be authenticated");

      const { data, error } = await supabase
        .from('subtasks')
        .update({ text: newText })
        .eq('id', subtaskId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      handleSuccess("Step text has been updated successfully.", { title: "Step updated" });
    },
    onError: (error: Error) => {
      handleError(error, { title: "Error updating step" });
    }
  });

  return {
    addSubtask: addSubtaskMutation.mutate,
    toggleSubtask: toggleSubtaskMutation.mutate,
    deleteSubtask: deleteSubtaskMutation.mutate,
    updateSubtaskDueDate: updateSubtaskDueDateMutation.mutate,
    updateSubtaskText: updateSubtaskTextMutation.mutate,
  };
};
